import React, { useState } from 'react';
import { ArrowRight, Users, DollarSign, GraduationCap, Globe, Target, CheckCircle, Star, Code, Shield, Cloud, Briefcase, Award, Building } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import WorkforceApplicationForm from '../components/WorkforceApplicationForm';

const WorkforceManagementPage = () => {
  const [applyFormOpen, setApplyFormOpen] = useState(false);
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      icon: Users,
      title: "Talent Acquisition",
      description: "Strategic recruitment solutions to find the right talent for your organization.",
      features: ["Executive Search", "Volume Hiring", "Specialized Roles", "Cultural Fit Assessment"]
    },
    {
      icon: GraduationCap,
      title: "Training & Development",
      description: "Comprehensive skill development programs to enhance workforce capabilities.",
      features: ["Technical Training", "Leadership Development", "Soft Skills", "Certification Programs"]
    },
    {
      icon: Globe,
      title: "Global Placement",
      description: "International career opportunities and cross-border talent solutions.",
      features: ["Overseas Opportunities", "Visa Assistance", "Cultural Integration", "Global Networks"]
    },
    {
      icon: Target,
      title: "Career Consulting",
      description: "Personalized career guidance and professional development strategies.",
      features: ["Career Planning", "Resume Building", "Interview Preparation", "Skill Assessment"]
    }
  ];

  const techSkills = [
    { name: "Software Development", icon: Code, level: 95 },
    { name: "Cybersecurity", icon: Shield, level: 88 },
    { name: "Cloud Computing", icon: Cloud, level: 92 },
    { name: "Project Management", icon: Briefcase, level: 90 }
  ];

  const stats = [
    { value: "500+", label: "Successful Placements" },
    { value: "50+", label: "Partner Companies" },
    { value: "95%", label: "Client Satisfaction" },
    { value: "30+", label: "Training Programs" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-20 bg-gradient-to-br from-navy-50 to-navy-100">
        <div className="max-w-7xl xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="text-center">
            <div className="inline-block px-6 py-2 bg-navy-100 text-brand-600 rounded-full text-sm xl:text-base font-medium mb-6 xl:mb-8">
              Professional Workforce Solutions
            </div>
            
            <h1 className="text-5xl md:text-6xl xl:text-7xl font-bold text-gray-900 mb-6 xl:mb-8">
              Workforce
              <span className="text-brand-600 block">Management</span>
            </h1>
            
            <p className="text-xl xl:text-2xl text-gray-600 max-w-3xl xl:max-w-4xl mx-auto mb-12 xl:mb-16 leading-relaxed">
              Connecting talent with opportunity through strategic workforce solutions, 
              comprehensive training programs, and global career development.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 xl:gap-8 justify-center items-center mb-16 xl:mb-20">
              <Button 
                onClick={() => setApplyFormOpen(true)}
                className="px-8 xl:px-12 py-4 xl:py-6 bg-navy-900 hover:bg-navy-800 text-white text-lg xl:text-xl font-semibold rounded-lg"
              >
                Get Started
                <ArrowRight className="w-5 h-5 xl:w-6 xl:h-6 ml-2" />
              </Button>
              
              <div className="flex items-center text-gray-600">
                <div className="flex -space-x-2 mr-4">
                  <div className="w-10 h-10 xl:w-12 xl:h-12 bg-navy-700 rounded-full border-2 border-white"></div>
                  <div className="w-10 h-10 xl:w-12 xl:h-12 bg-navy-700 rounded-full border-2 border-white"></div>
                  <div className="w-10 h-10 xl:w-12 xl:h-12 bg-navy-600 rounded-full border-2 border-white"></div>
                </div>
                <span className="text-sm xl:text-base">Trusted by 500+ professionals</span>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 xl:gap-12">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl xl:text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                  <div className="text-sm xl:text-base text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 xl:py-32 bg-white">
        <div className="max-w-7xl xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="text-center mb-16 xl:mb-20">
            <h2 className="text-4xl xl:text-5xl font-bold text-gray-900 mb-4 xl:mb-6">Our Services</h2>
            <p className="text-xl xl:text-2xl text-gray-600 max-w-2xl xl:max-w-3xl mx-auto">
              Comprehensive workforce solutions tailored to your needs
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 xl:gap-12">
            {/* Service Navigation */}
            <div className="lg:col-span-1">
              <div className="space-y-4 xl:space-y-6">
                {services.map((service, index) => (
                  <div
                    key={index}
                    onClick={() => setActiveService(index)}
                    className={`cursor-pointer p-6 xl:p-8 rounded-xl border-2 transition-all duration-200 ${
                      activeService === index
                        ? 'border-brand-500 bg-navy-50 shadow-lg'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-center">
                      <div className={`w-12 h-12 xl:w-14 xl:h-14 rounded-lg flex items-center justify-center mr-4 xl:mr-6 ${
                        activeService === index ? 'bg-navy-900' : 'bg-gray-100'
                      }`}>
                        <service.icon className={`w-6 h-6 xl:w-7 xl:h-7 ${
                          activeService === index ? 'text-white' : 'text-gray-600'
                        }`} />
                      </div>
                      <div>
                        <h3 className="font-semibold xl:text-lg text-gray-900">{service.title}</h3>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Active Service Details */}
            <div className="lg:col-span-2">
              <div className="bg-gray-50 rounded-xl p-8 xl:p-12">
                <div className="flex items-center mb-6 xl:mb-8">
                  <div className="w-16 h-16 xl:w-20 xl:h-20 bg-navy-900 rounded-xl flex items-center justify-center mr-6 xl:mr-8">
                    {React.createElement(services[activeService].icon, { className: "w-8 h-8 xl:w-10 xl:h-10 text-white" })}
                  </div>
                  <div>
                    <h3 className="text-2xl xl:text-3xl font-bold text-gray-900">{services[activeService].title}</h3>
                  </div>
                </div>

                <p className="text-gray-600 text-lg xl:text-xl mb-8 xl:mb-10 leading-relaxed">
                  {services[activeService].description}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 xl:gap-6 mb-8 xl:mb-10">
                  {services[activeService].features.map((feature, idx) => (
                    <div key={idx} className="flex items-center p-4 xl:p-6 bg-white rounded-lg border border-gray-200">
                      <CheckCircle className="w-5 h-5 xl:w-6 xl:h-6 text-brand-500 mr-3 xl:mr-4 flex-shrink-0" />
                      <span className="text-gray-700 xl:text-lg">{feature}</span>
                    </div>
                  ))}
                </div>

                <Button 
                  onClick={() => setApplyFormOpen(true)}
                  className="w-full bg-navy-900 hover:bg-navy-800 text-white py-3 xl:py-4 text-lg xl:text-xl rounded-lg"
                >
                  Learn More About This Service
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Skills Section */}
      <section className="py-20 xl:py-32 bg-gray-50">
        <div className="max-w-7xl xl:max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="text-center mb-16 xl:mb-20">
            <h2 className="text-4xl xl:text-5xl font-bold text-gray-900 mb-4 xl:mb-6">Technical Expertise</h2>
            <p className="text-xl xl:text-2xl text-gray-600 max-w-2xl xl:max-w-3xl mx-auto">
              Specialized training programs in high-demand technology skills
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 xl:gap-12">
            {techSkills.map((skill, index) => (
              <div key={index} className="bg-white rounded-xl p-6 xl:p-8 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4 xl:mb-6">
                  <skill.icon className="w-8 h-8 xl:w-10 xl:h-10 text-brand-600" />
                  <span className="text-2xl xl:text-3xl font-bold text-gray-900">{skill.level}%</span>
                </div>
                
                <h3 className="text-lg xl:text-xl font-semibold text-gray-900 mb-4 xl:mb-6">{skill.name}</h3>
                
                <div className="w-full bg-gray-200 rounded-full h-2 xl:h-3 mb-4 xl:mb-6">
                  <div 
                    className="bg-navy-900 h-2 xl:h-3 rounded-full transition-all duration-500"
                    style={{ width: `${skill.level}%` }}
                  ></div>
                </div>
                
                <Button 
                  size="sm" 
                  variant="outline"
                  className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 xl:py-3 xl:text-base"
                >
                  View Program
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 xl:py-32 bg-navy-900">
        <div className="max-w-4xl xl:max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 text-center">
          <h2 className="text-4xl xl:text-5xl font-bold text-white mb-6 xl:mb-8">
            Ready to Advance Your Career?
          </h2>
          <p className="text-xl xl:text-2xl text-navy-100 mb-12 xl:mb-16 max-w-2xl xl:max-w-4xl mx-auto">
            Join thousands of professionals who have transformed their careers 
            through our comprehensive workforce management solutions.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 xl:gap-8 justify-center">
            <Button 
              onClick={() => setApplyFormOpen(true)}
              className="px-8 xl:px-12 py-4 xl:py-6 bg-white text-brand-600 hover:bg-gray-100 text-lg xl:text-xl font-semibold rounded-lg"
            >
              Start Your Journey
            </Button>
            
            <Button 
              variant="outline" 
              className="px-8 xl:px-12 py-4 xl:py-6 border-2 border-white text-white hover:bg-white hover:text-brand-600 text-lg xl:text-xl font-semibold rounded-lg"
            >
              Contact Us
            </Button>
          </div>

          <div className="mt-12 xl:mt-16 flex justify-center items-center space-x-8 xl:space-x-12 text-navy-100">
            <div className="flex items-center">
              <Building className="w-5 h-5 xl:w-6 xl:h-6 mr-2" />
              <span className="xl:text-lg">Global Reach</span>
            </div>
            <div className="flex items-center">
              <Award className="w-5 h-5 xl:w-6 xl:h-6 mr-2" />
              <span className="xl:text-lg">Certified Programs</span>
            </div>
            <div className="flex items-center">
              <Star className="w-5 h-5 xl:w-6 xl:h-6 mr-2" />
              <span className="xl:text-lg">Expert Guidance</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      
      {/* Application Form Modal */}
      <WorkforceApplicationForm open={applyFormOpen} onOpenChange={setApplyFormOpen} />
    </div>
  );
};

export default WorkforceManagementPage;